import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import VideoComponent from "src/components/HomePage/VideoComponent";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import { isVideoSource } from "src/utils/mediaUtils";
import { FaVolumeHigh, FaVolumeXmark } from "react-icons/fa6";

const VideoCarousel = ({
  items = [],
  arrowIcon,
  onSlideChange,
  renderOverlay,
  renderTitle,
  sliderSettings = {},
  videoSoundControl = true,
  asNavFor = null, // Sync with another slider
  sliderRef: externalRef = null, // Allow parent to control ref
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isGlobalMuted, setIsGlobalMuted] = useState(true);
  const internalRef = useRef(null);
  const sliderRef = externalRef || internalRef;

  // Check if any videos have sound
  const hasAnyVideoWithSound = items.some(item => item.source && isVideoSource(item.source));

  // Handle slide change
  const handleBeforeChange = (_current, next) => {
    setCurrentSlide(next);
    if (onSlideChange) {
      onSlideChange(next, items[next]);
    }
  };

  // Default slider settings
  const defaultSettings = {
    dots: false,
    arrows: true,
    infinite: true,
    speed: 300,
    cssEase: "cubic-bezier(0.4, 0, 0.2, 1)",
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: false, // Use slide transition by default
    beforeChange: handleBeforeChange,
    nextArrow: <CustomArrow direction="next" arrowIcon={arrowIcon} />,
    prevArrow: <CustomArrow direction="prev" arrowIcon={arrowIcon} />,
    asNavFor: asNavFor, // Sync with another slider
  };

  const mergedSettings = { ...defaultSettings, ...sliderSettings };

  return (
    <CarouselContainer>
      <Slider {...mergedSettings} ref={sliderRef}>
        {items.map((item, index) => (
          <SlideWrapper key={index}>
            {renderTitle && <TitleWrapper>{renderTitle(item, index)}</TitleWrapper>}
            <VideoContainer>
              {item.platformImage && (
                <PlatformFrame>
                  <PlatformImage
                    src={item.platformImage}
                    alt={item.platformImageAlt || "Platform"}
                  />
                </PlatformFrame>
              )}
              {item.source && isVideoSource(item.source) && (
                <VideoComponent
                  desktop={item.source}
                  mobile={item.source}
                  soundControl={false}
                  externalMuted={isGlobalMuted || index !== currentSlide}
                  containerStyle={{
                    position: "absolute",
                    inset: 0,
                    margin: "auto",
                    zIndex: 2,
                    height: "100%",
                    width: "100%",
                    maxHeight: "none",
                    maxWidth: "100%",
                    paddingBottom: 0
                  }}
                  videoProps={{
                    style: {
                      objectFit: "contain",
                      pointerEvents: "none"
                    },
                    poster: item.posterImage,
                    "aria-label": item.imageAlt || item.name || "Video"
                  }}
                />
              )}
              {renderOverlay && renderOverlay(item, index)}
            </VideoContainer>
          </SlideWrapper>
        ))}
      </Slider>
      {videoSoundControl && hasAnyVideoWithSound && (
        <GlobalSoundToggle onClick={() => setIsGlobalMuted(!isGlobalMuted)}>
          {isGlobalMuted ? <FaVolumeXmark size={24} /> : <FaVolumeHigh size={24} />}
        </GlobalSoundToggle>
      )}
    </CarouselContainer>
  );
};

// Custom Arrow Component
const CustomArrow = ({ direction, arrowIcon, onClick }) => (
  <ArrowButton
    type="button"
    onClick={onClick}
    aria-label={direction === "next" ? "Next" : "Previous"}
    className={`slick-${direction}-custom`}
  >
    {arrowIcon ? (
      <ResponsiveImage
        src={arrowIcon}
        alt={direction === "next" ? "Next" : "Previous"}
        style={direction === "prev" ? { transform: "rotate(180deg)" } : {}}
      />
    ) : (
      <span>{direction === "next" ? ">" : "<"}</span>
    )}
  </ArrowButton>
);

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 520px;
  margin: 0 auto;

  @media (max-width: 991px) {
    max-width: 460px;
  }

  @media (max-width: 767px) {
    max-width: 100%;
  }

  .slick-slider {
    position: relative;
  }

  .slick-list {
    overflow: hidden;
  }

  .slick-track {
    display: flex;
  }

  .slick-slide {
    float: none;
    height: inherit;

    > div {
      height: 100%;
    }
  }
`;

const SlideWrapper = styled.div`
  outline: none;
`;

const TitleWrapper = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: 8px;
  pointer-events: none;
  position: relative;
  z-index: 10;
`;

const VideoContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-height: 640px;
  height: fit-content;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding-bottom: 0;
  z-index: 1;
  flex: 0 0 auto;
  min-height: 740px;

  @media (max-width: 991px) {
    min-height: 440px;
    height: 440px;
    width: 100%;
    max-width: 100%;
  }
`;

const PlatformFrame = styled.div`
  position: absolute;
  left: 50%;
  bottom: -12px;
  transform: translateX(-50%);
  z-index: 1;
  width: min(100%, 560px);
  height: 190px;
  overflow: hidden;
  pointer-events: none;
  z-index: 8;

  @media (max-width: 767px) {
    width: min(100%, 280px);
    height: 110px;
    bottom: -8px;
  }
`;

const PlatformImage = styled(ResponsiveImage)`
  position: absolute;
  left: 50%;
  bottom: -20px;
  transform: translateX(-44%);
  width: 188%;
  height: auto;
  pointer-events: none;

  @media (max-width: 767px) {
    bottom: -10px;
    width: 176%;
    transform: translateX(-40%);
  }
`;

const ArrowButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  line-height: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  flex: 0 0 auto;

  &.slick-prev-custom {
    left: 0;
  }

  &.slick-next-custom {
    right: 0;
  }

  span {
    font-size: 30px;
    line-height: 1;
    color: rgba(250, 225, 169, 0.9);
  }

  img {
    width: 74px;
    height: auto;
    display: block;
  }

  @media (max-width: 991px) {
    img {
      width: 58px;
    }
  }

  @media (max-width: 767px) {
    width: 60px;
    height: 60px;

    img {
      width: 60px;
    }
  }
`;

const GlobalSoundToggle = styled.button`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 25;

  svg {
    color: white;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  @media (max-width: 767px) {
    bottom: 15px;
    right: 15px;
  }
`;

export default VideoCarousel;
